import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";

import { Link, useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import FullScreenVideo from "~/components/common/FullScreenVideo";
import { Button } from "~/components/ui/Button";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/Carousel";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData, isEnabled } from "~/lib/utils";

export const sitemap = createSitemapFunction("");

export const handle = {
  navbar: {
    variant: "transparent" as const,
  },
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return getMetaData(data);
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "homepage";

    const locale = await getLocale(request);
    const lang = locale;

    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }

    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export default function Index() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();
  const { bookingDisabled } = useRouteLoaderData<any>("root");

  return (
    <div
      className={`
         relative
      `}
    >
      <main>
        {loaderData.page.acf?.hero && (
          <div>
            <FullScreenVideo
              className="w-full lg:h-screen h-[750px] object-cover object-center"
              autoplay={true}
              videoPreview={`/cms-asset/${loaderData.page.acf?.hero?.hero_file.id}`}
              videoSrc={`/cms-asset/${loaderData.page.acf?.hero?.hero_file.id}`}
              mobileVideo={
                loaderData.page.acf?.hero?.hero_file_mobile
                  ? `/cms-asset/${loaderData.page.acf?.hero?.hero_file_mobile.id}`
                  : undefined
              }
              mobileVideoPreview={
                loaderData.page.acf?.hero?.hero_file_mobile
                  ? `/cms-asset/${loaderData.page.acf?.hero?.hero_file_mobile.id}`
                  : undefined
              }
            />
          </div>
        )}

        {loaderData.page &&
          loaderData.page.acf?.hero &&
          loaderData.page.acf?.hero?.hero_carousel?.length > 0 && (
            <div className="relative">
              <Carousel className="w-full relative z-[1]">
                <CarouselContent>
                  {loaderData.page.acf?.hero?.hero_carousel?.map(
                    (_: any, index: number) => (
                      <CarouselItem key={index}>
                        <div
                          style={{
                            height: "calc(100vh - 112px)",
                          }}
                          className="w-full relative"
                        >
                          <img
                            alt=""
                            src={`/cms-asset/${_.image.id}`}
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute w-full h-full top-0 left-0">
                            <div className="px-20 mx-auto flex flex-row h-full justify-end">
                              <img
                                alt=""
                                src={`/cms-asset/${_.over_image.id}`}
                                className="absolute w-1/3 h-full object-contain object-center z-10"
                              />
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    )
                  )}
                </CarouselContent>
                <CarouselDots />
              </Carousel>
              <div className="absolute bottom-16 left-0 w-full flex items-center justify-center z-10">
                <img
                  onClick={() => {
                    window.scrollTo({
                      top: window.innerHeight,
                      behavior: "smooth",
                    });
                  }}
                  src="/images/mouse-scroll.png"
                  className="w-12 h-12 object-contain object-center animate-bounce cursor-pointer"
                />
              </div>
            </div>
          )}

        {/* Award badge right under the hero (CHLSPA-051), managed from the
            "Sezione premio" group of the Homepage in WP. Before that group
            exists (wp-files/migrations/2026-11-03-01) the section shows the
            2026 "positive" badge shipped with the site, which also serves as
            fallback when no image is picked. */}
        {(!loaderData.page?.acf?.award_section ||
          isEnabled(
            loaderData.page.acf.award_section.award_section_enable
          )) && (
          <section className="flex items-center justify-center pt-10 pb-4 lg:pt-14 lg:pb-8 px-4">
            <img
              src={
                loaderData.page?.acf?.award_section?.image?.id
                  ? `/cms-asset/${loaderData.page.acf.award_section.image.id}`
                  : "/images/winner-badge-home.png"
              }
              alt="World Spa Awards 2026 Winner - Italy's Best Day Spa"
              className="h-44 lg:h-64 w-auto"
            />
          </section>
        )}

        {loaderData.page?.acf?.text_section &&
          isEnabled(loaderData.page.acf?.text_section?.text_section_enable) && (
            <section
              className="flex flex-col items-center justify-center py-12 max-w-7xl mx-auto px-4"
              id={"section-1"}
            >
              <h4 className="text-secondary font-tenor-sans text-4xl uppercase">
                {loaderData.page.acf?.text_section.title}
              </h4>
              <h5 className="text-secondary text-center font-red-hat text-lg md:text-2xl uppercase tracking-[.5rem] lg:tracking-[.9rem]">
                {loaderData.page.acf?.text_section.subtitle}
              </h5>
              <hr className="h-0.5 bg-secondary w-44 mt-4" />
              <div
                className="text-center mt-4"
                dangerouslySetInnerHTML={{
                  __html: loaderData.page.acf?.text_section.description,
                }}
              />
              <Link
                to={
                  bookingDisabled
                    ? getMenuHref(i18n.language, "/coming-soon")
                    : getMenuHref(i18n.language, "/booking")
                }
              >
                <Button variant={"secondary"} className="mt-4 uppercase">
                  {t("Prenota ora")}
                </Button>
              </Link>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.alternate_media_description_section?.sections &&
          isEnabled(
            loaderData.page.acf?.alternate_media_description_section
              ?.alternate_media_description_section_enable
          ) &&
          loaderData.page.acf?.alternate_media_description_section?.sections
            .length > 0 && (
            <>
              {loaderData.page.acf.alternate_media_description_section.sections.map(
                (section: any, index: number) => {
                  const isEven = index % 2 === 0;
                  return (
                    <section key={index} className="w-full px-4 py-12">
                      <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto">
                        <div
                          className={
                            isEven ? "order-2 lg:order-2" : "order-2 lg:order-1"
                          }
                        >
                          {section.image && section.image.id && (
                            <img
                              alt={section.title || ""}
                              src={`/cms-asset/${section.image.id}`}
                              className="w-full h-auto object-cover object-center"
                            />
                          )}
                        </div>
                        <div
                          className={`flex flex-col ${isEven ? "order-1 lg:order-1" : "order-1 lg:order-2"
                            }`}
                        >
                          {section.title && (
                            <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                              {section.title}
                            </h4>
                          )}
                          {section.subtitle && (
                            <h5 className="text-secondary font-red-hat text-lg md:text-xl uppercase tracking-wider mt-2">
                              {section.subtitle}
                            </h5>
                          )}
                          {section.description && (
                            <>
                              <hr className="h-0.5 bg-secondary w-44 mt-4" />
                              <div
                                className="mt-4"
                                dangerouslySetInnerHTML={{
                                  __html: section.description,
                                }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </section>
                  );
                }
              )}
            </>
          )}

        {loaderData.page &&
          loaderData.page.acf?.about_us_section &&
          isEnabled(
            loaderData.page.acf?.about_us_section?.about_us_section_enable
          ) && (
            <section className="w-full bg-muted px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12">
                <div>
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf?.about_us_section.image.id}`}
                    className="w-96 h-96 object-contain object-center"
                  />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase max-w-xl">
                    {loaderData.page.acf?.about_us_section.title}
                  </h4>
                  <hr className="h-0.5 bg-secondary w-44 mt-4" />
                  <div
                    className="mt-4 h5-mb-8"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.about_us_section.description,
                    }}
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.separator_1 &&
          isEnabled(loaderData.page.acf?.separator_1?.separator_1_enable) && (
            <section
              className="bg-cover bg-center"
              style={{
                backgroundImage: `url("/cms-asset/${loaderData.page.acf?.separator_1?.background_image.id}")`,
              }}
            >
              <div className="bg-black bg-opacity-20">
                <div className="flex flex-col max-w-7xl mx-auto py-12 ">
                  <h4 className="text-muted font-tenor-sans text-xl lg:text-3xl text-center italic">
                    {loaderData.page.acf?.separator_1?.title}
                  </h4>
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.video_section &&
          isEnabled(
            loaderData.page.acf?.video_section?.video_section_enable
          ) && (
            <section className="px-4">
              <div className="flex flex-col max-w-7xl mx-auto py-12">
                <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl text-center uppercase">
                  {loaderData.page.acf?.video_section.title}
                </h4>
                <div
                  className="mt-4 max-w-3xl mx-auto text-center"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.video_section.description,
                  }}
                />
                <div className="mt-8 rounded-2xl overflow-hidden shadow">
                  <FullScreenVideo
                    videoPreview={`/cms-asset/${loaderData.page.acf?.video_section.video_preview?.id}`}
                    videoSrc={`/cms-asset/${loaderData.page.acf?.video_section.video?.id}`}
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.section_il_viaggio &&
          isEnabled(
            loaderData.page.acf?.section_il_viaggio?.section_il_viaggio_enable
          ) && (
            <section
              className="w-full lg:pt-96 lg:-mt-96 bg-white-nude px-4"
              style={{}}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12">
                <div className="flex flex-col items-start">
                  <h4 className="text-secondary font-tenor-sans text-4xl uppercase">
                    {loaderData.page.acf?.section_il_viaggio?.title}
                  </h4>
                  <hr className="h-0.5 bg-secondary w-44 mt-4" />

                  <div
                    className="mt-4"
                    dangerouslySetInnerHTML={{
                      __html:
                        loaderData.page.acf?.section_il_viaggio?.description,
                    }}
                  />
                  <Link to={"/worlds"}>
                    <Button
                      variant={"secondary"}
                      className="mt-8 rounded-full py-6 pl-5 pr-3 uppercase"
                    >
                      {t("Scopri il viaggio")}
                      <ChevronRight />
                    </Button>
                  </Link>
                </div>
                <div>
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf?.section_il_viaggio?.image?.id}`}
                    className="w-full h-96 object-cover object-center"
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.separator_2 &&
          isEnabled(loaderData.page.acf?.separator_2?.separator_2_enable) && (
            <section
              className="bg-cover bg-center"
              style={{
                backgroundImage: `url("/cms-asset/${loaderData.page.acf?.separator_2?.background_image.id}")`,
              }}
            >
              <div className="bg-black bg-opacity-20">
                <div className="flex flex-col max-w-7xl mx-auto py-12 ">
                  <h4 className="text-muted font-tenor-sans text-xl lg:text-3xl text-center italic">
                    {loaderData.page.acf?.separator_2?.title}
                  </h4>
                </div>
              </div>
            </section>
          )}
        {loaderData.page &&
          loaderData.page.acf?.images_slider_section &&
          loaderData.page.acf?.images_slider_section?.length > 0 && (
            <section className="container mx-auto mb-4">
              <Carousel opts={{ loop: true }} className="w-full relative">
                <CarouselContent>
                  {loaderData.page.acf?.images_slider_section?.map(
                    (item: any, index: number) => (
                      <CarouselItem key={index}>
                        <div className="w-full h-[500px] md:h-[700px]">
                          <img
                            alt=""
                            src={`/cms-asset/${item.image?.id || item.id}`}
                            className="w-full h-full object-cover object-center"
                          />
                        </div>
                      </CarouselItem>
                    )
                  )}
                </CarouselContent>
                <CarouselPrevious className="left-4" />
                <CarouselNext className="right-4" />
                <CarouselDots variant="secondary" />
              </Carousel>
            </section>
          )}

        {loaderData.page.acf?.booking_section && (
          <section className="max-w-7xl mx-auto w-full mb-12">
            <div
              className="bg-cover bg-center bg-no-repeat w-full"
              style={{
                backgroundImage: `url('/cms-asset/${loaderData.page.acf?.booking_section.background_image.id}')`,
              }}
            >
              <div className="bg-black bg-opacity-20 py-20 px-4">
                <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                  <h1 className="text-4xl text-center font-tenor-sans text-white">
                    {loaderData.page.acf?.booking_section.title}
                  </h1>
                  <div
                    className="text-white mt-4 text-xl text-center"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.booking_section.description,
                    }}
                  />
                  <div className="flex items-center justify-center space-x-2">
                    <Link to={getMenuHref(i18n.language, "/booking")}>
                      <Button variant={"muted"} className="mt-4">
                        {t("Prenota")}
                      </Button>
                    </Link>
                    <Link to={getMenuHref(i18n.language, "/shop")}>
                      <Button variant={"muted"} className="mt-4">
                        {t("Regala")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* HERE */}
      </main>
    </div>
  );
}
